import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, catchError, filter, map, of, take, timeout } from 'rxjs';
import { AuthActions, authFeature } from '../../features/auth/state';
import {
  PRE_REGISTERED_LANDING_ROUTE,
  canAccessStudentPortal,
  isAdmissionsRoute,
  isPreRegisteredStatus,
} from '../utils/auth-status';
import { REFRESH_TIMEOUT_MS, TokenRefreshService } from '../services/token-refresh.service';
import { readJwtExpiry } from '../utils/jwt';

// Bound on the wait for the permissions of a disallowed-role user; shorter than a token refresh.
export const PERMISSIONS_WAIT_TIMEOUT_MS = 10_000;

export const authGuard: CanActivateFn = (_route, state) => {
  const store = inject(Store);
  const router = inject(Router);
  const token = store.selectSignal(authFeature.selectToken);
  const role = store.selectSignal(authFeature.selectRole);
  const status = store.selectSignal(authFeature.selectStatus);

  if (token()) {
    if (!hasDisallowedPortalRole(role(), status())) {
      return renewExpiredToken(token(), inject(TokenRefreshService), router);
    }

    // Without permissions the session is closed: keeping the token would make noAuthGuard on
    // /login wait for the same permissions again.
    const endSession = (): UrlTree => {
      store.dispatch(AuthActions.logout());
      return router.createUrlTree(['/login']);
    };

    return store.select(authFeature.selectPermissionsLoaded).pipe(
      filter(Boolean),
      take(1),
      timeout(PERMISSIONS_WAIT_TIMEOUT_MS),
      map(() => {
        const canManageCorrections = store
          .selectSignal(authFeature.selectPermissions)()
          .includes('academic.grade_corrections.manage');
        if (canManageCorrections) {
          return isGradeCorrectionsUrl(router, state.url)
            ? true
            : router.createUrlTree(['/admin/grade-corrections']);
        }
        return endSession();
      }),
      catchError(() => of(endSession())),
    );
  }

  return router.createUrlTree(['/login']);
};

// A page opened with a stored JWT that already expired renews it before any protected route
// renders, so the first screen does not fail and recover. Only a rejected refresh token stops
// the navigation; a connection problem or a slow server lets the app start with the tokens kept.
function renewExpiredToken(
  jwt: string | null,
  refresh: TokenRefreshService,
  router: Router,
): boolean | Observable<boolean | UrlTree> {
  const expiry = readJwtExpiry(jwt);

  if (expiry === null || expiry > Date.now() || refresh.msUntilRenewalAllowed() > 0) {
    return true;
  }

  return refresh.renew(null, { silent: true }).pipe(
    timeout(REFRESH_TIMEOUT_MS),
    map((outcome) =>
      outcome.status === 'session-ended' ? router.createUrlTree(['/login']) : true,
    ),
    catchError(() => of(true)),
  );
}

// Only the grade corrections page is open to this user: the rest of /admin has no permission guard.
// Comparing the primary segments keeps query params and fragments valid and rejects /adminX.
function isGradeCorrectionsUrl(router: Router, url: string): boolean {
  const segments = router.parseUrl(url).root.children['primary']?.segments ?? [];
  return (
    segments.length === 2 &&
    segments[0].path === 'admin' &&
    segments[1].path === 'grade-corrections'
  );
}

export const noAuthGuard: CanActivateFn = () => {
  const store = inject(Store);
  const router = inject(Router);
  const token = store.selectSignal(authFeature.selectToken);
  const status = store.selectSignal(authFeature.selectStatus);
  const role = store.selectSignal(authFeature.selectRole);

  if (token()) {
    if (!hasDisallowedPortalRole(role(), status())) {
      return router.createUrlTree([
        isPreRegisteredStatus(status(), role()) ? PRE_REGISTERED_LANDING_ROUTE : '/dashboard',
      ]);
    }

    return store.select(authFeature.selectPermissionsLoaded).pipe(
      filter(Boolean),
      take(1),
      map(() => {
        const canManageCorrections = store
          .selectSignal(authFeature.selectPermissions)()
          .includes('academic.grade_corrections.manage');
        if (canManageCorrections) {
          return router.createUrlTree(['/admin/grade-corrections']);
        }
        store.dispatch(AuthActions.logout());
        return true;
      }),
    );
  }

  return true;
};

export const admissionsOnlyGuard: CanActivateChildFn = (_route, state) => {
  const store = inject(Store);
  const router = inject(Router);
  const status = store.selectSignal(authFeature.selectStatus);
  const role = store.selectSignal(authFeature.selectRole);

  if (!isPreRegisteredStatus(status(), role())) {
    return true;
  }

  return isAdmissionsRoute(state.url) ? true : router.createUrlTree([PRE_REGISTERED_LANDING_ROUTE]);
};

function hasDisallowedPortalRole(
  role: string | null | undefined,
  status: string | null | undefined,
): boolean {
  return Boolean(role?.trim()) && !canAccessStudentPortal(role, status);
}
